
import { Component, Prop } from "vue-property-decorator";
import { EpgItem, MyChannel } from "@/services/apiService/api.types";
import { Action, Getter, Mutation } from "vuex-class";
import { Namespace } from "@/store/types";
import SpatialNavigation from "spatial-navigation-js";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import {
  ChannelsPageGetter,
  ChannelsPageMutation,
  InfoChannelData,
  ModalType,
  ViewType
} from "@/components/channelsPage/types";
import { RouteName } from "@/router/types";
import {
  ChannelChangeAction,
  ChannelChangeMutation
} from "@/components/channelChange/types";
import { ChannelEpgParams, OverlayAction } from "@/components/overlay/types";
import dayjs from "dayjs";
import { digitCount } from "@/helpers/utils";
import { mixins } from "vue-class-component";
import { EpgHelpers } from "@/mixins/EpgHelpers";
import { Categories } from "@/helpers/constants";
import { EventBus } from "@/main";
import LockIcon from "@/assets/svg/svg-settings/LockIcon.vue";
import { LocaleChannelsPageEpgCard } from "@/plugins/i18n/types";

@Component({
  components: { LockIcon }
})
export default class EpgCard extends mixins(EpgHelpers) {
  @Prop() readonly channel?: MyChannel;
  @Prop() readonly channelIndex?: number;
  @Prop() readonly channelCategory?: Categories;
  @Prop() readonly channelView?: ViewType;

  @Action(ChannelChangeAction.UpdateSource, {
    namespace: Namespace.ChannelChange
  })
  updateSource!: (src: string) => void;

  @Action(ChannelChangeAction.UpdateOffset, {
    namespace: Namespace.ChannelChange
  })
  updateOffset!: (offset: number) => void;

  @Action(OverlayAction.UpdateChannelEpg, {
    namespace: Namespace.Overlay
  })
  updateChannelEpg!: (channelEpgParams?: ChannelEpgParams) => Promise<void>;

  @Mutation(ChannelsPageMutation.setCardControls, {
    namespace: Namespace.ChannelsPage
  })
  setControlsActive!: (cardControls: boolean) => void;

  @Mutation(ChannelsPageMutation.setPlayedChannelId, {
    namespace: Namespace.ChannelsPage
  })
  setPlayedChannelId!: (channelId?: number) => void;

  @Mutation(ChannelsPageMutation.setPlayedChannelIndex, {
    namespace: Namespace.ChannelsPage
  })
  setPlayedChannelIndex!: (index?: number) => void;

  @Mutation(ChannelsPageMutation.setPlayedChannelCategory, {
    namespace: Namespace.ChannelsPage
  })
  setPlayedChannelCategory!: (category?: Categories) => void;

  @Mutation(ChannelsPageMutation.setPlayedChannelView, {
    namespace: Namespace.ChannelsPage
  })
  setPlayedChannelView!: (channelView?: ViewType) => void;

  @Mutation(ChannelChangeMutation.SetChannelId, {
    namespace: Namespace.ChannelChange
  })
  setChannelId!: (channelId: number) => void;

  @Mutation(ChannelsPageMutation.setActiveChannelOverlayInfo, {
    namespace: Namespace.ChannelsPage
  })
  setActiveChannelOverlayInfo!: (channelInfo?: InfoChannelData) => void;

  @Mutation(ChannelsPageMutation.setActiveInfoScreenEpgItem, {
    namespace: Namespace.ChannelsPage
  })
  setActiveInfoScreenEpgItem!: (epgItem?: EpgItem) => void;

  @Mutation(ChannelsPageMutation.setShowInfoScreen, {
    namespace: Namespace.ChannelsPage
  })
  setShowInfoScreen!: (showInfoScreen: boolean) => void;

  @Mutation(ChannelsPageMutation.setShowEpgScreen, {
    namespace: Namespace.ChannelsPage
  })
  setShowEpgScreen!: () => void;

  @Getter(ChannelsPageGetter.GetPlayedChannelId, {
    namespace: Namespace.ChannelsPage
  })
  getPlayedChannelId!: number | undefined;

  @Getter(ChannelsPageGetter.getShowEpgScreen, {
    namespace: Namespace.ChannelsPage
  })
  getShowEpgScreen!: boolean;

  @Getter(ChannelsPageGetter.getShowInfoScreen, {
    namespace: Namespace.ChannelsPage
  })
  getShowInfoScreen!: boolean;

  @Getter(ChannelsPageGetter.getActiveInfoScreenEpgItem, {
    namespace: Namespace.ChannelsPage
  })
  getActiveInfoScreenEpgItem!: EpgItem | undefined;

  showControls = false;
  focusedControl = 0;

  channelEpg: EpgItem[] = [];
  activeEpgItem: EpgItem = {
    id: 0,
    title: "",
    epgEndtime: "",
    epgStarttime: "",
    channel_id: 0,
    description: "",
    image: ""
  };

  card: HTMLElement | null = null; //var that stores reference to this card in the DOM
  isCardFocused = false;

  modalTypes = ModalType;

  LocaleChannelsPageEpgCard = LocaleChannelsPageEpgCard;

  async mounted(): Promise<void> {
    this.card = document.getElementById("channel" + this.channel?.id);
    if (this.card) {
      this.card.addEventListener("focusout", this.focusChanged);
    }

    if (this.channel) {
      //fetching epg list and active item
      this.channelEpg = await this.fetchChannelEpg(this.channel.id);
      const activeEpgItemTemp = this.updateActiveEpgItem(this.channelEpg);
      if (activeEpgItemTemp) {
        this.activeEpgItem = activeEpgItemTemp;
      }
    }

    //for tracking where we are on the timeline and update epgItem if we are out of bounds
    setInterval(() => {
      const currentTime = dayjs();
      this.secondsElapsed++;
      if (currentTime.isAfter(this.activeEpgItem.epgEndtime)) {
        const activeEpgItemTemp = this.updateActiveEpgItem(this.channelEpg);
        if (activeEpgItemTemp) {
          this.activeEpgItem = activeEpgItemTemp;
        }
      }
    }, 1000);

    //focusing previously played channel and setting the data required
    if (
      this.getPlayedChannelId &&
      this.getPlayedChannelId === this.channel?.id &&
      !this.getShowEpgScreen &&
      !this.getShowInfoScreen
    ) {
      SpatialNavigation.focus("#channel" + this.channel.id);
      this.setControlsActive(false);
      this.setPlayedChannelId();
      this.setPlayedChannelIndex();
      this.setPlayedChannelCategory();
      this.setPlayedChannelView();
    }

    //focusing channel after closing overlay
    EventBus.$on("infoOverlayClosed", (channelId: number) => {
      if (
        this.channel &&
        this.channel.id === channelId &&
        !this.getShowEpgScreen
      ) {
        setTimeout(() => {
          SpatialNavigation.focus("#channel" + this.channel?.id);
          SpatialNavigation.resume();
          this.setControlsActive(false);
        }, 50);
      }
    });

    //focusing channel after closing overlay
    EventBus.$on("epgOverlayClosed", (channelId: number) => {
      if (this.channel && this.channel.id === channelId) {
        setTimeout(() => {
          SpatialNavigation.focus("#channel" + this.channel?.id);
          SpatialNavigation.resume();
          this.setControlsActive(false);
        }, 50);
      }
    });

    //plays focused channel if overlay play btn was triggered(more convenient then copying play channel logic to every overlay)
    EventBus.$on("playChannel", (channelId: number) => {
      if (this.channel && this.channel.id === channelId) {
        this.playChannel();
      }
    });
  }

  get epgIcon(): string {
    return require("/src/assets/svg/svg-home/09-epg.svg");
  }

  get InfoIcon(): string {
    return require("/src/assets/svg/svg-home/10-info.svg");
  }

  get imgPlaceholder(): string {
    return require("/src/assets/images/square-image-placeholder.jpg");
  }

  get channelNum(): string {
    let channelNum = "";
    if (this.channel) {
      for (let i = 0; i < 3 - digitCount(this.channel?.id); i++) {
        channelNum += "0";
      }
      return channelNum + this.channel.id + ".";
    }

    return "000";
  }

  handleCardControls(event: KeyboardEvent): void {
    SpatialNavigation.pause();
    switch (keyCodeFinder(event.keyCode)) {
      case KeySet.KEY_ENTER:
        if (!this.showControls) {
          this.openControls();
          return;
        }
        switch (this.focusedControl) {
          case 0:
            this.playChannel();
            break;
          case 1:
            this.openModalScreen(this.modalTypes.EpgModal);
            break;
          case 2:
            this.openModalScreen(this.modalTypes.InfoModal);
            break;
        }
        break;
      case KeySet.KEY_RIGHT:
        if (this.focusedControl < 2) {
          this.focusedControl++;
        }
        break;
      case KeySet.KEY_LEFT:
        if (this.focusedControl > 0) {
          this.focusedControl--;
        }
        break;
      case KeySet.KEY_BACK:
        this.showControls = false;
        this.setControlsActive(false);
        SpatialNavigation.resume();
        break;
    }
  }

  openModalScreen(modalType: ModalType): void {
    if (this.channel) {
      const channelInfo: InfoChannelData = {
        channelName: this.channel.name,
        channelId: this.channel.id,
        channelCatchup: !!this.channel.catchup
      };

      this.setActiveChannelOverlayInfo(channelInfo);
      if (modalType === ModalType.InfoModal) {
        this.setActiveInfoScreenEpgItem(this.activeEpgItem);
        this.setShowInfoScreen(true);
      }
      if (modalType === ModalType.EpgModal) {
        this.setShowEpgScreen();
      }
    }
  }

  openControls(): void {
    this.showControls = true;
    this.setControlsActive(true);
  }

  focusChanged(): void {
    if (document.activeElement?.id !== "channel" + this.channel?.id) {
      this.showControls = false;
      this.isCardFocused = false;
    }
  }

  async playChannel(): Promise<void> {
    if (this.channel) {
      //setting player and overlay required info
      this.updateSource(this.channel.videoLive);
      this.setChannelId(this.channel.id);

      const activeEpgItem = this.updateActiveEpgItem(this.channelEpg);
      let offset = 0;
      if (
        this.getActiveInfoScreenEpgItem &&
        activeEpgItem &&
        this.getActiveInfoScreenEpgItem.id !== activeEpgItem.id
      ) {
        offset = dayjs().diff(
          this.getActiveInfoScreenEpgItem.epgStarttime,
          "second"
        );
      }
      this.updateOffset(offset);
      await this.updateChannelEpg({
        date: dayjs(this.getActiveInfoScreenEpgItem?.epgStarttime).format(
          "YYYY-MM-DD"
        )
      });

      //used upon component load to focus defined channels card if one is set
      this.setPlayedChannelId(this.channel.id);
      this.setPlayedChannelIndex(this.channelIndex);
      this.setPlayedChannelCategory(this.channelCategory);
      this.setPlayedChannelView(this.channelView);

      await this.$router.push({ name: RouteName.Player });
    }
  }

  beforeDestroy(): void {
    if (this.card) {
      this.card.removeEventListener("focusout", this.focusChanged);
    }
    EventBus.$off("playChannel");
  }
}
